import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { storableError } from '../../util/errors';
import { types as sdkTypes } from '../../util/sdkLoader';

// ================ Action types ================ //

export const FETCH_EVENT_LISTINGS_REQUEST = 'app/TicketListingsPage/FETCH_EVENT_LISTINGS_REQUEST';
export const FETCH_EVENT_LISTINGS_SUCCESS = 'app/TicketListingsPage/FETCH_EVENT_LISTINGS_SUCCESS';
export const FETCH_EVENT_LISTINGS_ERROR = 'app/TicketListingsPage/FETCH_EVENT_LISTINGS_ERROR';
export const CLEAR_EVENT_LISTINGS = 'app/TicketListingsPage/CLEAR_EVENT_LISTINGS';

// ================ Reducer ================ //

const initialState = {
  fetchInProgress: false,
  fetchError: null,
  listings: [],
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case FETCH_EVENT_LISTINGS_REQUEST:
      return {
        ...state,
        fetchInProgress: true,
        fetchError: null,
      };
    case FETCH_EVENT_LISTINGS_SUCCESS:
      return {
        ...state,
        fetchInProgress: false,
        listings: payload,
      };
    case FETCH_EVENT_LISTINGS_ERROR:
      return {
        ...state,
        fetchInProgress: false,
        fetchError: payload,
      };
    case CLEAR_EVENT_LISTINGS:
      return {
        ...state,
        listings: [],
        fetchError: null,
      };
    default:
      return state;
  }
}

// ================ Action creators ================ //

export const fetchEventListingsRequest = () => ({
  type: FETCH_EVENT_LISTINGS_REQUEST,
});

export const fetchEventListingsSuccess = listings => ({
  type: FETCH_EVENT_LISTINGS_SUCCESS,
  payload: listings,
});

export const fetchEventListingsError = e => ({
  type: FETCH_EVENT_LISTINGS_ERROR,
  payload: e,
  error: true,
});

export const clearEventListings = () => ({
  type: CLEAR_EVENT_LISTINGS,
});

// ================ Thunks ================ //

/**
 * Fetch ALL marketplace listings to filter by event name client-side.
 * This allows flexible name-based matching without requiring specific IDs.
 *
 * If ticketmasterId or attractionId is provided, use those for filtering.
 * Otherwise, fetch all published listings and filter by name in the component.
 */
export const fetchEventListings = params => (dispatch, getState, sdk) => {
  const {
    ticketmasterId,
    ticketmasterAttractionId,
    eventName,
    perPage = 100,
    fetchAll = false,
    fallbackToFetchAll = false,
  } = params || {};

  dispatch(fetchEventListingsRequest());

  const baseQueryParams = {
    include: ['author', 'author.profileImage', 'images'],
    'fields.listing': ['title', 'description', 'price', 'publicData', 'metadata'],
    per_page: perPage,
    states: ['published'],
    // Exclude testing-bids listings from ticket buying search results
    pub_listingType: 'buying-tickets',
  };

  const dispatchSuccess = response => {
    const listings = response?.data?.data || [];
    if (response) {
      dispatch(addMarketplaceEntities(response));
    }
    dispatch(fetchEventListingsSuccess(listings));
    return listings;
  };

  const dispatchError = error => {
    console.error('❌ [TicketListingsPage.duck] Error fetching listings:', error);
    const formatted = storableError(error);
    dispatch(fetchEventListingsError(formatted));
    return [];
  };

  const queryByFilters = filters => sdk.listings.query({ ...baseQueryParams, ...filters });

  // If fetchAll is true or no IDs provided, fetch ALL published listings
  if (fetchAll || (!ticketmasterId && !ticketmasterAttractionId)) {
    return queryByFilters(eventName ? { keywords: eventName } : {})
      .then(response => dispatchSuccess(response))
      .catch(dispatchError);
  }

  const queryByTicketmasterId = () => {
    if (!ticketmasterId) {
      dispatchSuccess(null);
      return Promise.resolve([]);
    }
    return queryByFilters({ pub_ticketmasterId: ticketmasterId })
      .then(response => {
        const listings = dispatchSuccess(response);
        // Fallback: if nothing matched by ticketmasterId but we want to allow name/date matching,
        // fetch all listings so the component can filter them client-side.
        if (fallbackToFetchAll && (!listings || listings.length === 0)) {
          return queryByFilters(eventName ? { keywords: eventName } : {}).then(resp =>
            dispatchSuccess(resp)
          );
        }
        return listings;
      })
      .catch(dispatchError);
  };

  // If attractionId is provided, try that first (more specific)
  if (ticketmasterAttractionId) {
    return queryByFilters({ pub_ticketmasterAttractionId: ticketmasterAttractionId })
      .then(response => {
        const listings = dispatchSuccess(response);
        // If we found listings by attractionId, use those.
        if (listings.length > 0) {
          return listings;
        }

        // If nothing matched by attractionId and we have a ticketmasterId, try that next.
        if (ticketmasterId) {
          return queryByTicketmasterId();
        }

        // As a last resort, if asked, fetch ALL listings so the component can do name/date matching.
        if (fallbackToFetchAll) {
          return queryByFilters({}).then(resp => dispatchSuccess(resp));
        }
        return listings;
      })
      .catch(error => {
        // If attractionId search fails and we have ticketmasterId, try that
        if (ticketmasterId) {
          return queryByTicketmasterId();
        }
        return dispatchError(error);
      });
  }

  // No attractionId, just use ticketmasterId
  return queryByTicketmasterId();
};

/**
 * Fetch Sharetribe listings by UUID(s) from URL (e.g. ?shareTribeListings=uuid1,uuid2).
 * Used when opening /tickets?shareTribeListings=... on refresh or direct link so we don't rely on location.state.
 */
export const fetchSharetribeListingsByIds = listingIds => (dispatch, getState, sdk) => {
  if (!Array.isArray(listingIds) || listingIds.length === 0) {
    dispatch(fetchEventListingsSuccess([]));
    return Promise.resolve([]);
  }
  const ids = listingIds.map(id => String(id).trim()).filter(Boolean);
  if (ids.length === 0) {
    dispatch(fetchEventListingsSuccess([]));
    return Promise.resolve([]);
  }

  dispatch(fetchEventListingsRequest());

  const fetchOne = id => {
    const uuid = id.length === 36 && id.indexOf('-') === 4 ? new sdkTypes.UUID(id) : id;
    return sdk.listings
      .show({
        id: uuid,
        include: ['author', 'author.profileImage', 'images'],
      })
      .then(response => {
        if (response && response.data) {
          dispatch(addMarketplaceEntities(response));
          return response.data.data || null;
        }
        return null;
      })
      .catch(err => {
        console.warn('🎫 [TicketListingsPage.duck] Failed to fetch listing', id, err);
        return null;
      });
  };

  return Promise.all(ids.map(fetchOne))
    .then(results => results.filter(Boolean))
    .then(listings => {
      dispatch(fetchEventListingsSuccess(listings));
      return listings;
    })
    .catch(err => {
      console.error('❌ [TicketListingsPage.duck] fetchSharetribeListingsByIds error', err);
      dispatch(fetchEventListingsError(storableError(err)));
      return [];
    });
};
